import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Slider from 'react-slick';
import ImageComponent from '../../atoms/image-component/image-component';
import styles from './carousel.module.scss';
import breakpoints from '../../../styles/partials/breakpoints.module.scss';
import Video from '../../atoms/video/video';
import IVideo from '../../../models/viewModels/base/IVideo';
import IImage from '../../../models/viewModels/base/IImage';
import { buildVideoPosterUrl } from '../media-gallery/media-gallery';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import { Breakpoint } from '../../../constants/breakpoints';
import Link from 'next/link';

export type Props = {
  carouselItems: Array<IVideo | IImage>;
  enableBorder: boolean;
  imageSize?: 'contain' | 'cover';
  aspectRatio: '16:9' | '1:1' | '4:3' | '3:2';
  id?: string;
  videoPreload?: boolean;
  isSidebarNavigation?: boolean;
};

type BtnProps = {
  height?: number;
  onClick?: () => void;
  className?: string;
  style?: any;
};

type SlideWrapperProps = {
  linkUrl: string;
  className: string;
  children: ReactNode;
};

const CustomBtn = ({ height, onClick, className, style }: BtnProps) => (
  <button
    className={className}
    aria-label={className}
    onClick={onClick}
    style={{ ...style }}
  ></button>
);

const SlideWrapper = ({ linkUrl, className, children }: SlideWrapperProps) => {
  return linkUrl ? (
    <Link className={className} href={linkUrl}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );
};

const MIN_SLIDES = 2;

const breakpointSlides: Array<{ width: number; slides: number }> = [
  { width: parseInt(breakpoints.sm), slides: 3 },
  { width: parseInt(breakpoints.md), slides: 4 },
  { width: parseInt(breakpoints.lg), slides: 5 },
];

function getBreakpointSlides(width: number) {
  let result = MIN_SLIDES;
  for (const breakpointSlide of breakpointSlides) {
    if (width < breakpointSlide.width) {
      result = breakpointSlide.slides;
      break;
    }
  }

  return result;
}

function getHasEnoughSlides(slides: number, width: number) {
  return slides >= getBreakpointSlides(width);
}

const getPadding = (aspectRatio: string): number => {
  const [a, b] = aspectRatio.split(':');
  return (+b / +a) * 100;
};

export default function Carousel({
  carouselItems,
  enableBorder,
  aspectRatio,
  imageSize = 'cover',
  id,
  isSidebarNavigation=false,
  videoPreload,
}: Props) {
  const [buttonHeight, setButtonHeight] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const [hasEnoughSlides, setHasEnoughSlides] = useState(true);

  useWindowResize(
    (newWidth) => setHasEnoughSlides(getHasEnoughSlides(carouselItems.length, newWidth)),
    100,
  );

  const carouselRef = useRef(null);

  useEffect(() => {
    setLoading(false);
  }, []);

  const renderData = useMemo(() => {
    const hasCaptions = carouselItems.some((el) => el.caption);

    const contentStyle: CSSProperties = {
      paddingTop: `${getPadding(aspectRatio)}%`,
    };

    const sliderSettings = {
      slidesToShow: isSidebarNavigation ? 3 : 4,
      slidesToScroll: 1,
      dots: true,
      arrows: true,
      infinite: false,
      nextArrow: <CustomBtn height={buttonHeight} />,
      prevArrow: <CustomBtn height={buttonHeight} />,
      responsive: [
        {
          breakpoint: Breakpoint.Large,
          settings: {
            slidesToShow: isSidebarNavigation ? 2 : 3,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: Breakpoint.Medium,
          settings: {
            slidesToShow: isSidebarNavigation ? 1 : 2,
            slidesToScroll: 1,
            arrows: false,
          },
        },
        {
          breakpoint: Breakpoint.Small,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
          },
        },
      ],
    };

    return { hasCaptions, contentStyle, sliderSettings };
  }, [carouselItems, aspectRatio]);

  const onReInit = useCallback(() => {
    if (!renderData.hasCaptions) {
      return;
    }

    const imageHeight = carouselRef.current.querySelector('.carousel-item-content').offsetHeight;
    setButtonHeight(Math.round(imageHeight));
  }, []);

  if (loading) {
    return (
      <div ref={carouselRef} className={styles.carousel} id={id}>
        <LoadingSpinner src={'/public/next-assets/loaders/into.svg'} />
      </div>
    );
  }

  return (
    <div
      ref={carouselRef}
      className={`carousel_custom-navs ${hasEnoughSlides ? 'carousel-custom' : null} ${styles.carousel}`}
      id={id}
    >
      <Slider {...renderData.sliderSettings} onReInit={onReInit}>
        {carouselItems?.map((carouselItem, index) => (
          <CarouselItemMemoised
            key={index}
            carouselItem={carouselItem}
            enableBorder={enableBorder}
            contentStyle={renderData.contentStyle}
            imageSize={imageSize}
            videoPreload={videoPreload}
          />
        ))}
      </Slider>
    </div>
  );
}
const CarouselItem: React.FC<{
  carouselItem: IVideo | IImage;
  enableBorder: boolean;
  contentStyle;
  imageSize: string;
  videoPreload: boolean;
}> = ({ carouselItem, enableBorder, contentStyle, imageSize, videoPreload }) => (
  <SlideWrapper
    key={carouselItem.id}
    linkUrl={carouselItem.resourceType === 'image' ? (carouselItem as IImage).linkUrl : null}
    className={`${styles['carousel-item']} carousel-item`}
  >
    <div className={enableBorder ? styles['carousel-item-border'] : null}>
      <div
        className={`${styles['carousel-item-content']} carousel-item-content`}
        style={contentStyle}
      >
        {carouselItem.resourceType === 'image' ? (
          <ImageComponent
            src={carouselItem.secureUrl}
            objectFit={imageSize}
            altText={`${(carouselItem as IImage)?.alt ?? ``}`}
          />
        ) : (
          <Video
            src={carouselItem.secureUrl}
            videoPreload={videoPreload}
            poster={
              (carouselItem as IVideo)?.previewImageSecureURL
                ? (carouselItem as IVideo)?.previewImageSecureURL
                : buildVideoPosterUrl(carouselItem.secureUrl)
            }
          />
        )}
      </div>
      {carouselItem.caption || carouselItem.title ? (
        <div className='text-center px-3'>
          <h4>{carouselItem.title}</h4>
          <p>{carouselItem.caption}</p>
        </div>
      ) : null}
    </div>
  </SlideWrapper>
);

const CarouselItemMemoised = React.memo(CarouselItem);
